import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auleloApi, auleloAuth } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Lock, Loader2 } from "lucide-react";

export default function AuleloFirstLogin() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [agentName, setAgentName] = useState("Aulelo");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!auleloAuth.getToken()) { nav("/Kittanna", { replace: true }); return; }
    auleloApi.get("/me").then(({ data }) => {
      setMe(data);
      setAgentName(data.agent_name || "Aulelo");
      if (!data.first_login) nav("/aulelo/console", { replace: true });
    }).catch(() => nav("/Kittanna", { replace: true }));
  }, [nav]);

  const submit = async (e) => {
    e?.preventDefault();
    if (next !== confirm) { toast.error("Passkeys don't match"); return; }
    if (next.length < 6) { toast.error("Min 6 characters"); return; }
    if (next.toLowerCase() === "kittanna") { toast.error("Choose a different passkey from the default"); return; }
    setBusy(true);
    try {
      await auleloApi.post("/rotate-passkey", { current_passkey: "Kittanna", new_passkey: next });
      if (agentName.trim() && agentName !== me.agent_name) {
        await auleloApi.patch("/config", { agent_name: agentName.trim() });
      }
      toast.success(`Welcome, ${agentName}. New URL: /${next}/`);
      // Force re-auth on the new URL
      auleloAuth.clear();
      nav(`/${next}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotation failed");
    } finally { setBusy(false); }
  };

  if (!me) return null;

  return (
    <div className="min-h-screen bg-black text-zinc-200 flex items-center justify-center p-6">
      <div className="fixed inset-0 pointer-events-none" style={{
        background: "repeating-linear-gradient(0deg, rgba(0,255,140,0.02) 0px, rgba(0,255,140,0.02) 1px, transparent 1px, transparent 3px)"
      }} />
      <form onSubmit={submit} data-testid="aulelo-first-login-form" className="relative max-w-md w-full border border-emerald-700/40 bg-black/90 shadow-[0_0_60px_rgba(0,255,140,0.08)]">
        <div className="border-b border-emerald-700/30 px-6 py-4 flex items-center gap-3">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-emerald-300">first contact</div>
        </div>
        <div className="px-6 py-8 space-y-5">
          <div>
            <h1 className="font-mono text-2xl font-black tracking-tight text-emerald-200">Set up your agent</h1>
            <p className="text-xs text-zinc-500 mt-2 font-mono">
              You're using the default passkey. Choose a fresh one and (optionally) rename your agent. The new passkey becomes your URL.
            </p>
          </div>
          <div>
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">Agent name</Label>
            <Input value={agentName} onChange={(e) => setAgentName(e.target.value)} maxLength={40} data-testid="aulelo-first-name" className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-11 font-mono" />
          </div>
          <div>
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">New passkey (6+ chars, becomes the URL)</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" data-testid="aulelo-first-next" className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-11 font-mono tracking-widest" />
          </div>
          <div>
            <Label className="font-mono uppercase tracking-widest text-[10px] text-zinc-400">Confirm</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="aulelo-first-confirm" className="mt-1 rounded-none bg-black border-emerald-700/40 text-emerald-100 h-11 font-mono tracking-widest" />
          </div>
          <Button type="submit" disabled={busy || !next || !confirm} data-testid="aulelo-first-submit" className="w-full rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-[0.25em] text-xs h-12">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
            Seal & enter
          </Button>
        </div>
      </form>
    </div>
  );
}
